import React, { useMemo } from 'react';

type MarketRow = {
  propertyType?: string;
  bedrooms?: number | string;
  median?: number | null;
  medianDaysOnMarket?: number | null;
  averageDaysOnMarket?: number | null;
  totalListings?: number | null;
};

type InsightMarket = {
  saleByPropertyType?: MarketRow[];
  rentalByPropertyType?: MarketRow[];
  saleByBedrooms?: MarketRow[];
  rentalByBedrooms?: MarketRow[];
};

const money = (value: number) => `$${Math.round(value).toLocaleString()}`;
const displayType = (value?: string) =>
  String(value || 'Property')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const InvestorInsightsCard: React.FC<{ market?: InsightMarket | null }> = ({ market }) => {
  const insights = useMemo(() => {
    if (!market) return [];
    const saleTypes = market.saleByPropertyType || [];
    const rentalTypes = market.rentalByPropertyType || [];
    const saleBeds = market.saleByBedrooms || [];
    const rentalBeds = market.rentalByBedrooms || [];
    const rentalByBed = new Map(rentalBeds.map((row) => [String(row.bedrooms), row]));

    const deepestSaleType = [...saleTypes].sort((a, b) => Number(b.totalListings) - Number(a.totalListings))[0];
    const matchingRentalType = rentalTypes.find((row) => row.propertyType === deepestSaleType?.propertyType);
    const liquidRental = [...rentalBeds]
      .filter((row) => Number(row.totalListings) > 0)
      .sort((a, b) => {
        const aDom = Number(a.medianDaysOnMarket ?? a.averageDaysOnMarket) || 999;
        const bDom = Number(b.medianDaysOnMarket ?? b.averageDaysOnMarket) || 999;
        return Number(b.totalListings) / bDom - Number(a.totalListings) / aDom;
      })[0];
    const yieldCandidates = saleBeds
      .map((sale) => {
        const rental = rentalByBed.get(String(sale.bedrooms));
        const salePrice = Number(sale.median) || 0;
        const rent = Number(rental?.median) || 0;
        return {
          bedrooms: sale.bedrooms,
          salePrice,
          rent,
          yieldPct: salePrice > 0 ? rent * 12 * 100 / salePrice : 0,
          listings: Math.min(Number(sale.totalListings) || 0, Number(rental?.totalListings) || 0),
        };
      })
      .filter((row) => row.salePrice > 0 && row.rent > 0 && row.listings > 2);
    const bestYield = [...yieldCandidates].sort((a, b) => b.yieldPct - a.yieldPct)[0];
    const lowestEntry = [...saleBeds]
      .filter((row) => Number(row.median) > 0 && Number(row.totalListings) > 1)
      .sort((a, b) => Number(a.median) - Number(b.median))[0];
    const thinBeds = saleBeds
      .filter((row) => Number(row.totalListings) > 0 && Number(row.totalListings) <= 2)
      .sort((a, b) => Number(a.bedrooms) - Number(b.bedrooms));

    return [
      deepestSaleType && {
        icon: '⌂',
        color: '#1498a0',
        title: `${displayType(deepestSaleType.propertyType)} properties have the deepest sale market:`,
        detail: `${Number(deepestSaleType.totalListings).toLocaleString()} sale listings${matchingRentalType ? ` and ${Number(matchingRentalType.totalListings).toLocaleString()} rental listings` : ''}.`,
      },
      liquidRental && {
        icon: '▣',
        color: '#2888dc',
        title: `${liquidRental.bedrooms}-bedroom rentals look relatively liquid:`,
        detail: `median rent ${money(Number(liquidRental.median))}, ${Math.round(Number(liquidRental.medianDaysOnMarket ?? liquidRental.averageDaysOnMarket))} DOM, and ${Number(liquidRental.totalListings).toLocaleString()} listings.`,
      },
      bestYield && {
        icon: '↗',
        color: '#43a668',
        title: `${bestYield.bedrooms}-bedroom homes show strong apparent cash-flow potential:`,
        detail: `${money(bestYield.rent)} median rent vs. ${money(bestYield.salePrice)} median sale price (${bestYield.yieldPct.toFixed(1)}% gross yield).`,
      },
      lowestEntry && {
        icon: '⚿',
        color: '#7650d8',
        title: `${lowestEntry.bedrooms}-bedroom homes are the lowest reliable purchase-price entry point:`,
        detail: `${money(Number(lowestEntry.median))} median sale price.`,
      },
      thinBeds.length > 0 && {
        icon: '!',
        color: '#f05a35',
        title: `Use caution with ${thinBeds.map((row) => `${row.bedrooms}-bedroom`).join(' and ')} data:`,
        detail: `each highlighted sale category has only ${thinBeds.map((row) => Number(row.totalListings)).join(' and ')} listing${thinBeds.length === 1 && Number(thinBeds[0].totalListings) === 1 ? '' : 's'}.`,
      },
    ].filter(Boolean) as Array<{ icon: string; color: string; title: string; detail: string }>;
  }, [market]);

  if (!insights.length) return null;

  return (
    <div style={{ border: '1px solid #e8eef3', borderRadius: 14, background: '#fff', boxShadow: '0 2px 10px rgba(15,23,42,.06)', padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#12858d', fontSize: 18, fontWeight: 800, marginBottom: 14 }}>
        <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#dff4f5', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>💡</span>
        9. Automated Investor Insights
      </div>
      <div>
        {insights.map((insight, index) => (
          <div key={insight.title} style={{ display: 'grid', gridTemplateColumns: '62px 34px 1fr', gap: 10, alignItems: 'center', border: '1px solid #e7edf2', borderRadius: 10, padding: 12, marginBottom: 8 }}>
            <div style={{ width: 54, height: 54, borderRadius: '50%', background: `${insight.color}18`, color: insight.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 25, fontWeight: 700 }}>{insight.icon}</div>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: insight.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800 }}>{index + 1}</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#17233d', lineHeight: 1.3 }}>{insight.title}</div>
              <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.45, marginTop: 3 }}>{insight.detail}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InvestorInsightsCard;
